import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronRight, MoreVertRounded } from '@mui/icons-material'
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  Link,
  MenuItem,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import { MenuButton } from 'components/common/MenuButton'
import { useSetHeaderBreadcrumbs } from '../components/Navigation/HeaderBreadcrumbsContext'
import { useSiteRFPDetails } from 'api/hooks/useSiteRFPDetails'
import { SiteRFPPhaseChips } from 'components/common/SiteRFPPhaseChips'
import { SiteRFPPhaseAPIResponse } from '@black-bear-energy/black-bear-energy-common'
import { getSiteRFPPhaseDisplayName } from 'components/helpers/DocumentExchange/getSiteRFPPhaseDisplayName'
import { SiteDetailsCard } from 'components/Sites/SiteDetailsCard'
import { ProjectDetailsCard } from 'components/Sites/ProjectDetailsCard'
import { ContractsDetailsCard } from 'components/Sites/ContractsDetailsCard'
import { SiteRequirementsCard } from 'components/Sites/SiteRequirementsCard'
import { DocumentRequirementsCard } from 'components/Sites/DocumentRequirementsCard'
import { MilestonesCard } from 'components/Sites/MilestonesCard'
import { ContactsCard } from 'components/common/ContactsCard'

const linkStyle = {
  '&:hover, &:focus': {
    color: rucksack.palette.primary.main,
  },
}

const steps = Object.values(SiteRFPPhaseAPIResponse)
  .filter(
    (phase) =>
      phase !== SiteRFPPhaseAPIResponse.PreRFP &&
      phase !== SiteRFPPhaseAPIResponse.DeriskingAndPermits
  )
  .map((phase) => ({
    key: phase,
    label: getSiteRFPPhaseDisplayName(phase),
  }))

export function SiteDetail() {
  const { siteRFPId } = useParams<{ siteRFPId: string }>()
  const {
    isPending: isPendingSiteRFPDetails,
    data: siteRFPDetails,
    isError,
    isFetching,
  } = useSiteRFPDetails(Number(siteRFPId))

  const activeStep = steps.findIndex(
    (step) =>
      step.key === siteRFPDetails?.phase ||
      (siteRFPDetails?.phase === SiteRFPPhaseAPIResponse.DeriskingAndPermits &&
        step.key === SiteRFPPhaseAPIResponse.Derisking)
  )

  const breadcrumbs = useMemo(
    () => (
      <Breadcrumbs separator={<ChevronRight />}>
        <Link
          href="/sites"
          color="text.primary"
          underline="hover"
          sx={linkStyle}
        >
          Sites
        </Link>
        {isPendingSiteRFPDetails && <Skeleton width={100} />}
        {!isPendingSiteRFPDetails && siteRFPDetails && (
          <Typography color="text.primary">{siteRFPDetails.name}</Typography>
        )}
      </Breadcrumbs>
    ),
    [isPendingSiteRFPDetails, siteRFPDetails]
  )

  useSetHeaderBreadcrumbs(breadcrumbs)

  return (
    <>
      {isError && !siteRFPDetails && (
        <Alert severity="error" variant="outlined" sx={{ marginTop: 2 }}>
          Failed to load site. Please refresh the page.
        </Alert>
      )}
      {isError && siteRFPDetails && !isFetching && (
        <Alert severity="warning" variant="outlined" sx={{ marginTop: 2 }}>
          Could not refresh data. Showing previous results.
        </Alert>
      )}
      {isPendingSiteRFPDetails ? (
        <Skeleton width={100} />
      ) : (
        <Typography
          variant="overline"
          sx={{ color: rucksack.palette.text.secondary }}
        >
          {siteRFPDetails?.customerRFPName}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {isPendingSiteRFPDetails ? (
            <Skeleton width={260} height={40} sx={{ transform: 'none' }} />
          ) : (
            <>
              <Typography variant="h4">{siteRFPDetails?.name}</Typography>
              <MenuButton
                align="left"
                icon={<MoreVertRounded />}
                menuSx={{
                  '& .MuiMenuItem-root': {
                    display: 'flex',
                    padding: '6px 16px',
                    alignItems: 'center',
                    gap: '12px',
                  },
                }}
                keepOpen={false}
              >
                {siteRFPDetails?.prospectorLink && (
                  <MenuItem
                    component={Link}
                    href={`${process.env.REACT_APP_PROSPECTOR_URL}/rfps/${siteRFPDetails?.prospectorLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="text.primary"
                    underline="hover"
                    sx={linkStyle}
                  >
                    View in Prospector
                  </MenuItem>
                )}

                {siteRFPDetails?.customerRFPId && (
                  <MenuItem
                    component={Link}
                    href={`/document-exchange/projects/${siteRFPDetails?.customerRFPId}/sites/${siteRFPDetails?.id}`}
                    color="text.primary"
                    underline="hover"
                    sx={linkStyle}
                  >
                    Go to Document Exchange
                  </MenuItem>
                )}
              </MenuButton>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          {siteRFPDetails?.siteDropped && (
            <Chip
              label="Dropped"
              variant="outlined"
              sx={{ fontSize: '14px' }}
            />
          )}
          {siteRFPDetails?.phase && (
            <SiteRFPPhaseChips phase={siteRFPDetails?.phase} />
          )}
        </Box>
      </Box>
      <Stepper
        sx={{ margin: '16px 0px' }}
        colorScheme="secondary"
        activeStep={activeStep}
      >
        {steps.map((step) => (
          <Step
            key={step.key}
            active={isStepActive(step.key, siteRFPDetails?.phase)}
          >
            <StepLabel hideCompletedLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SiteDetailsCard
          siteRFPDetails={siteRFPDetails}
          isFetching={isFetching}
        />
        <ProjectDetailsCard
          siteRFPDetails={siteRFPDetails}
          isFetching={isFetching}
        />
        <ContractsDetailsCard
          siteRFPDetails={siteRFPDetails}
          isFetching={isFetching}
        />
        <SiteRequirementsCard
          siteRFPDetails={siteRFPDetails}
          isFetching={isFetching}
        />
        <DocumentRequirementsCard
          docRequirements={siteRFPDetails?.documentRequirements}
          customerRFPId={siteRFPDetails?.customerRFPId}
          siteRFPId={Number(siteRFPId)}
          hasContractsType={siteRFPDetails?.contractType != null}
          isFetching={isFetching}
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <MilestonesCard
            milestones={siteRFPDetails?.milestones}
            isFetching={isFetching}
          />
          <ContactsCard
            siteRFPDetails={siteRFPDetails}
            isFetching={isFetching}
          />
        </Box>
      </Box>
    </>
  )
}

function isStepActive(
  step: SiteRFPPhaseAPIResponse,
  phase?: SiteRFPPhaseAPIResponse
): boolean {
  if (!phase) {
    return false
  }
  if (phase === SiteRFPPhaseAPIResponse.DeriskingAndPermits) {
    return (
      step === SiteRFPPhaseAPIResponse.Derisking ||
      step === SiteRFPPhaseAPIResponse.Permits
    )
  }
  return step === phase
}
